import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CardCmp from 'src/components/CardCmp';
import MessageCard from 'src/components/EntityChatView/components/MessageCard';
import MessageCardSkeleton from 'src/components/EntityChatView/components/MessageCardSkeleton';
import { groupMessages } from 'src/components/EntityChatView/helpers';
import { IEntityChatMessage } from 'src/components/EntityChatView/interface';
import { getSharedQuoteMessages } from 'src/services/ShareLinkService';

/**
 * SharedMessagesView — read-only chat widget for the public share-link page.
 *
 * Reuses the auth ChatView's MessageCard + groupMessages so the visual output
 * is pixel-identical to the logged-in widget. The composer, sockets, and
 * mark-as-read flows are intentionally absent — share viewers are anonymous
 * and the BE share endpoint is read-only.
 *
 * The BE returns raw ChatGroupMessage rows enriched with a `sender` object;
 * we normalize them here to the IEntityChatMessage shape MessageCard expects
 * (mirrors quoteChat.slice's normalizeMessage to keep behavior consistent).
 */

interface IProps {
    /** Refetch when quoteId changes (page-level, after token verification). */
    quoteId: number | string | undefined;
    entityType?: string;
    /** When true, removes the fixed height so the component fills its flex container. */
    fullHeight?: boolean;
}

const normalizeMessage = (raw: any): IEntityChatMessage => {
    const senderData = raw?.sender || raw?.userInfo || {};

    let content = '';

    if (typeof raw?.content === 'object' && raw?.content !== null) {
        content = raw.content?.message || '';
    } else {
        content = raw?.content || raw?.message || '';
    }

    return {
        id: raw?.id?.toString() || `${Date.now()}`,
        content,
        userInfo: {
            id: raw?.senderId || senderData?.id || '',
            firstName: senderData?.firstName || '',
            lastName: senderData?.lastName || '',
            imageUrl: senderData?.imageUrl || '',
            image: senderData?.imageUrl && senderData?.image ? `${senderData.imageUrl}${senderData.image}` : senderData?.image || '',
            user_status: senderData?.user_status || 'offline',
        },
        createdAt: raw?.createdAt || new Date().toISOString(),
        isUnread: false,
        isOptimistic: false,
        hasError: false,
        messageType: raw?.messageType || 'text',
        attachments: raw?.attachments || [],
        senderType: raw?.senderType || 'user',
        chatType: raw?.chatType || 'customer',
    };
};

const getFormattedDateTime = (date: any): string => {
    if (!date) return '';
    const m = moment(date);
    if (!m.isValid()) return '';

    const diffInSeconds = moment().diff(m, 'seconds');
    if (diffInSeconds < 60) return 'just now';

    return m.format('h:mm A');
};

const SharedMessagesView: React.FC<IProps> = ({ quoteId, entityType, fullHeight }) => {
    const [messages, setMessages] = useState<IEntityChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string>('');
    // MessageCard takes refs + an onResend handler — both unused for read-only,
    // but the prop API requires them. Stable empty values are fine.
    const messageRefs = useRef<any[]>([]);

    const noopResend = () => {};

    const messagesList = useMemo(() => groupMessages(messages), [messages]);

    useEffect(() => {
        if (!quoteId) return;

        let cancelled = false;
        setIsLoading(true);
        setErrorMessage('');

        (async () => {
            try {
                const response: any = await getSharedQuoteMessages({ perPage: 50, ...(entityType ? { entityType } : {}) });
                if (cancelled) return;
                const data = response?.data ?? response;
                const raw: any[] = data?.messages ?? [];
                // BE returns DESC (newest first); MessageCard renders top-to-bottom
                // chronologically, so reverse to oldest-first like the auth flow.
                setMessages(raw.map(normalizeMessage).reverse());
            } catch (error: any) {
                if (cancelled) return;
                setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to load messages');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [quoteId]);

    return (
        <CardCmp headerTitle="Messages" isCardContentWithoutSpacing isHeaderWithBg childrenClassName="">
            <div className={`flex-1 flex flex-col overflow-auto custom-scrollbar-v2 p-4 ${fullHeight ? 'h-full' : 'h-[256px] lg:h-[192px] max-h-[256px] lg:max-h-[192px]'}`}>
                {isLoading && messagesList.length === 0 && <MessageCardSkeleton />}

                {!isLoading && errorMessage && <p className="text-sm text-textSecondary text-center my-auto">{errorMessage}</p>}

                {!isLoading && !errorMessage && messagesList.length === 0 && <p className="text-sm text-textSecondary text-center my-auto">No messages yet.</p>}

                {messagesList.length > 0 && (
                    <ul className="w-full mt-auto">
                        {messagesList.map((message, index) => (
                            <MessageCard
                                key={message.id || index}
                                message={message}
                                // null currentUser → every message renders as "from someone else",
                                // which is correct for an anonymous public viewer.
                                currentUser={null}
                                getFormattedDateTime={getFormattedDateTime}
                                messageRefs={messageRefs}
                                chatMembers={[]}
                                onResend={noopResend}
                                mainIndex={index}
                                isFromWidget
                            />
                        ))}
                    </ul>
                )}
            </div>
        </CardCmp>
    );
};

export default SharedMessagesView;
